import { FC /*, ReactNode*/, useState, useEffect, ReactNode } from 'react';
import { BrowserRouter, Switch, Route as ReactRoute } from 'react-router-dom';
import { useStore } from 'hooks/useRootStore';
import { observer } from 'mobx-react-lite';
import Dashboard from 'containers/Dashboard';
import SignIn from 'containers/SignIn';
import Settings from 'containers/Settings';
import Logs from 'containers/Logs';
import Import from 'containers/Import';
import Users from 'containers/Users';
import Googles from 'containers/Googles';
import User from 'containers/User';
import Groups from 'containers/Groups';
import Group from 'containers/Group';
import Departments from 'containers/Departments';
import Department from 'containers/Department';
import UpgradeLicense from 'containers/UpgradeLicense';
import Payments from 'containers/Payments';
import Administrators from 'containers/Administrators';
import UserForgotPassword from 'containers/UserForgotPassword';
import UserResetPassword from 'containers/UserResetPassword';
import AccessDeniedPage from 'containers/AccessDeniedPage';
import UserIdRecovery from 'containers/UserIdRecovery';
import AdminForgotPassword from 'containers/AdminForgotPassword';
import AdminResetPassword from 'containers/AdminResetPassword';
import Profile from 'containers/Profile';
import EndUserSelfRegistration from 'containers/EndUserSelfRegistration';
import ViewEndUserRegistration from 'containers/ViewEndUserRegistration';
import { TabsEditRequest } from 'components/Tables';
import UsersUpdatedInformation from 'containers/UsersUpdatedInformation';
import NewPassword from 'containers/NewPassword';
import StatusPage from 'containers/StatusPage';
import SignInEndUser from 'containers/SignInEndUser/SignInEndUser';
import EnterDomain from 'containers/SignIn/EnterDomain';
import SwaggerUI from 'components/Swagger';
import GroupsTable from 'containers/GroupsTable';
import DepartmentsTable from 'containers/DepartmentsTable';
import UsersDetail, { ManagerDetail } from 'containers/UsersDetail';
import DepartmentUserDetail from 'containers/DepartmentUserDetail';
import Admin from 'containers/Admin';
import Apps from 'containers/Apps';
import AppAuth from 'containers/SignIn/AppAuth';
import ZoomAuth from 'containers/SignIn/ZoomAuth';
import Zoom from 'containers/Zoom';
import Microsoft from 'containers/Microsoft/Microsoft';
import MicrosoftAuth from 'containers/SignIn/MicrosoftAuth';
import Github from 'containers/Github';
import GithubAuth from 'containers/SignIn/GithubAuth';
import Authenticate from 'containers/SignIn/AuthSignIn';
import CodeUrl from 'containers/SignIn/CodeUrl';
import RecoveryCode from 'containers/SignIn/RecoveryCode';
import Devices from 'containers/Devices';
import Password from 'containers/Password';
import Callback from 'containers/Callback';
import OuthPage from 'containers/SignIn/OuthPage';
import { ClientRegisterForm } from 'containers/Register';
import { getTenantFromSubdomain, isValidTenant } from 'utils/tenantUtils';
import { AppMenuWraper } from 'components/AppMenuWraper';
import { Redirect } from 'react-router';
import { OdicInfoTable } from 'components/Tables/OdicTable';
import EndUserDashboard from 'containers/EndUserDashboard';
import RegisterCallback from 'containers/RegisterCallback/RegisterCallback';
import DepartmentManagerTable from 'containers/DepartmentUserDetail/DepartmentManagerTable';
import AppDetail from 'containers/UsersDetail/AppDetail';
import DepartmentAppTable from 'containers/DepartmentUserDetail/DepartmentAppTable';
import { EndUserRequestTable } from 'components/Tables/UserEditRequestTable/EndUserRequestTable';

interface RouteWrapperProps {
  type: 'public' | 'admin' | 'end-user';
  component: ReactNode;
  showAppMenu?: boolean;
  title?: string;
  extraInfo?: string;
  disclaimer?: {
    show: boolean;
    component: ReactNode;
  };
}

interface RouteConfig {
  type: 'public' | 'admin' | 'end-user';
  path: string;
  page: {
    showAppMenu?: boolean;
    component: ReactNode;
    title?: string;
    extraInfo?: string;
    disclaimer?: {
      show: boolean;
      component: ReactNode;
    };
  };
}

const RouteWrapper: FC<RouteWrapperProps> = ({ type, component, showAppMenu, title, extraInfo, disclaimer }) => {
  const {
    userStore: { token },
  } = useStore();

  const tenant = getTenantFromSubdomain();
  const isAdminRoute = type === 'admin' || type === 'end-user';

  // If it's an admin route and there's a tenant, validate it
  if (isAdminRoute && tenant && !isValidTenant(tenant)) {
    return <Redirect to={'/'} />;
  }

  const wrapedComponent = (
    <AppMenuWraper show={showAppMenu} title={title} extraInfo={extraInfo}>
      {component}
    </AppMenuWraper>
  );

  switch (type) {
    case 'admin':
      if (!token) {
        return <Redirect to={'/'} />;
      }
      if (!tenant) {
        return <Redirect to={'/'} />;
      }
      return <>{disclaimer?.show ? disclaimer.component : wrapedComponent}</>;
    case 'end-user':
      if (!token) {
        return <Redirect to={'/'} />;
      }
      if (!tenant) {
        return <Redirect to={'/'} />;
      }
      return <>{disclaimer?.show ? disclaimer.component : wrapedComponent}</>;
    default:
      return <>{wrapedComponent}</>;
  }
};

export const Router: FC = observer(() => {
  const {
    endUserConfigStore: { registartionPermited, recoveryPermited },
    logsTableStore: { logsDateRangeDescription },
    // paymentsTableStore: { paymentsDateRangeDescription },
    ldapStore: { clickedDepartment, clickedGroup },
    userStore: { user },
  } = useStore();

  const [CurrentGroup, setCurrentGroup] = useState<any>(clickedGroup);
  const [CurrentDepartment, setCurrentDepartment] = useState<any>(clickedDepartment);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentGroup(JSON.parse(localStorage.getItem('CurrentGroup') as string));
      setCurrentDepartment(JSON.parse(localStorage.getItem('CurrentDepartment') as string));
    }, 3000);

    // Clean up the timeout when the component unmounts to avoid memory leaks
    return () => clearTimeout(timeoutId);
  }, [CurrentGroup, CurrentDepartment]);

  // Check if current URL has subdomain and is /register
  const tenant = getTenantFromSubdomain();
  // const domain = getDomain();
  const pages: RouteConfig[] = [
    {
      type: 'admin',
      path: '/apps/google',
      page: {
        showAppMenu: true,
        title: 'Google Workspace',
        component: <Googles />,
        disclaimer: {
          show: !!(user && user?.permission === 'EndUser'),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/apps/zoom',
      page: {
        showAppMenu: true,
        title: 'Zoom Workspace',
        component: <Zoom />,
        disclaimer: {
          show: !!(user && user?.permission === 'EndUser'),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/apps/microsoft',
      page: {
        showAppMenu: true,
        title: 'Microsoft Entra / Office 365 Workspace',
        component: <Microsoft />,
        disclaimer: {
          show: !!(user && user?.permission === 'EndUser'),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/apps/github',
      page: {
        showAppMenu: true,
        title: 'Github Workspace',
        component: <Github />,
        disclaimer: {
          show: !!(user && user?.permission === 'EndUser'),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/odic/info',
      page: {
        showAppMenu: true,
        title: 'ODIC Workspace',
        component: <OdicInfoTable />,
        disclaimer: {
          show: !!(user && user?.permission === 'EndUser'),
          component: <AccessDeniedPage />,
        },
      },
    },
    { type: 'public', path: '/.well-known/openid-configuration', page: { component: <OuthPage /> } },
    { type: 'public', path: '/google', page: { component: <AppAuth /> } },
    { type: 'public', path: '/admin/app/callback/google', page: { component: <Callback /> } },
    { type: 'public', path: '/auth/callback/google', page: { component: <RegisterCallback /> } },
    { type: 'public', path: '/zoom', page: { component: <ZoomAuth /> } },
    { type: 'public', path: '/microsoft', page: { component: <MicrosoftAuth /> } },
    { type: 'public', path: '/github', page: { component: <GithubAuth /> } },
    { type: 'public', path: '/login', page: { component: <SignIn /> } },
    // { type: 'public', path: '/oauth/*', page: { component: <div style={{ padding: '50px', textAlign: 'center' }}><h1>OAuth Page</h1><p>OAuth authentication in progress...</p></div> } },
    { type: 'public', path: '/reset-password', page: { component: <AdminResetPassword /> } },
    { type: 'public', path: '/admin/reset-password', page: { component: <AdminResetPassword /> } },
    { type: 'public', path: '/authenticate', page: { component: <Authenticate /> } },
    { type: 'public', path: '/two-factor/recovery', page: { component: <RecoveryCode /> } },
    { type: 'public', path: '/2-fa', page: { component: <CodeUrl /> } },
    { type: 'public', path: '/login/admin-domain', page: { component: <EnterDomain /> } },
    { type: 'public', path: '/login/user', page: { component: <SignInEndUser /> } },
    { type: 'public', path: '/admin/forgot-password', page: { component: <AdminForgotPassword /> } },
    // {
    //   type: 'public',
    //   path: '/:domain?/email-invite',
    //   page: {
    //     showAppMenu: true,
    //     title: 'Email',
    //     component: <EmailInvite />,
    //   },
    // },

    // (tenant !== 'app') ? <EndUserSelfRegistration /> :
    {
      type: 'public',
      path: '/register',
      page: {
        component: (() => {
          return tenant !== 'app' ? <EndUserSelfRegistration /> : <ClientRegisterForm />;
        })(),
        disclaimer: { show: Boolean(tenant && !registartionPermited), component: <AccessDeniedPage /> },
      },
    },
    {
      type: 'public',
      path: '/restapi/public/clients/swagger/docs/',
      page: {
        component: <SwaggerUI />,
      },
    },
    {
      type: 'admin',
      path: '/admin/view-register-enduser/:id',
      page: {
        showAppMenu: true,
        title: 'Review Enduser Registration',
        component: <ViewEndUserRegistration />,
      },
    },
    {
      type: 'admin',
      path: '/admin/users-updated-information/:id',
      page: {
        showAppMenu: true,
        title: 'New User Request',
        component: <UsersUpdatedInformation />,
        disclaimer: { show: !!(user && user?.permission === 'EndUser'), component: <AccessDeniedPage /> },
      },
    },
    {
      type: 'public',
      path: '/user/forgot-userid',
      page: {
        component: <UserIdRecovery />,
        disclaimer: { show: !recoveryPermited, component: <AccessDeniedPage /> },
      },
    },
    {
      type: 'public',
      path: '/user/forgot-password',
      page: {
        component: <UserForgotPassword type={'general'} />,
        disclaimer: { show: !recoveryPermited, component: <AccessDeniedPage /> },
      },
    },
    {
      type: 'public',
      path: '/new-password/:domain?',
      page: {
        component: <NewPassword />,
        disclaimer: { show: !recoveryPermited, component: <AccessDeniedPage /> },
      },
    },
    {
      type: 'public',
      path: '/status',
      page: {
        component: (
          <StatusPage status='pass' onEventClick={() => console.log('pass')} heading='Password Changed Successfully!' />
        ),
        disclaimer: { show: !recoveryPermited, component: <AccessDeniedPage /> },
      },
    },
    {
      type: 'public',
      path: '/user/reset-password',
      page: {
        component: <UserResetPassword />,
        disclaimer: { show: !recoveryPermited, component: <AccessDeniedPage /> },
      },
    },
    {
      type: 'admin',
      path: '/payments',
      page: {
        showAppMenu: true,
        title: 'Payments',
        component: <Payments />,
        disclaimer: { show: !!(user && user?.permission === 'EndUser'), component: <AccessDeniedPage /> },
      },
    },
    {
      type: 'admin',
      path: '/reports',
      page: {
        showAppMenu: true,
        title: 'Reports',
        extraInfo: logsDateRangeDescription,
        component: <Logs />,
      },
    },
    {
      type: 'admin',
      path: '/upgrade-license',
      page: {
        showAppMenu: true,
        title: 'Upgrade License',
        component: <UpgradeLicense alowPlanSelection />,
        disclaimer: {
          show: !!(
            user &&
            (user?.permission === 'EndUser' || user?.permission === 'Manager' || user?.permission === 'SystemAdmin')
          ),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/settings',
      page: {
        showAppMenu: true,
        title: 'Settings',
        component: <Settings />,
        // disclaimer: { show: !!(user && user?.permission === 'EndUser'), component: <AccessDeniedPage /> },
      },
    },
    {
      type: 'admin',
      path: '/import',
      page: {
        showAppMenu: true,
        title: 'Upload',
        component: <Import />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/apps',
      page: {
        showAppMenu: true,
        title: 'Apps',
        component: <Apps />,
      },
    },
    {
      type: 'admin',
      path: '/devices',
      page: {
        showAppMenu: true,
        title: 'Devices',
        component: <Devices />,
      },
    },
    {
      type: 'admin',
      path: '/groups',
      page: {
        showAppMenu: true,
        title: 'Groups',
        component: <Groups />,
      },
    },
    {
      type: 'admin',
      path: `/group/edit`,
      page: {
        showAppMenu: true,
        title: `Edit Group`,
        component: <Group type='edit' />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: `/group/add`,
      page: {
        showAppMenu: true,
        title: `Add Group`,
        component: <Group />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/group/view',
      page: {
        showAppMenu: true,
        title: `View Group`,
        component: <Group type='view' />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/group/users',
      page: {
        showAppMenu: true,
        title: CurrentGroup?.cn
          ? `View Group: ${CurrentGroup?.cn}`
          : clickedGroup?.cn
          ? `View Group: ${clickedGroup?.cn}`
          : 'View Group',
        component: <GroupsTable />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/group/add-users',
      page: {
        showAppMenu: true,
        title: CurrentGroup?.cn
          ? `View Group: ${CurrentGroup?.cn}`
          : clickedGroup?.cn
          ? `View Group: ${clickedGroup?.cn}`
          : 'View Group',
        component: <UsersDetail />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/group/add-managers',
      page: {
        showAppMenu: true,
        title: CurrentGroup?.cn
          ? `View Group: ${CurrentGroup?.cn}`
          : clickedGroup?.cn
          ? `View Group: ${clickedGroup?.cn}`
          : 'View Group',
        component: <ManagerDetail />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/group/add-apps',
      page: {
        showAppMenu: true,
        title: CurrentGroup?.cn
          ? `View Group: ${CurrentGroup?.cn}`
          : clickedGroup?.cn
          ? `View Group: ${clickedGroup?.cn}`
          : 'View Group',
        component: <AppDetail />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/department/users',
      page: {
        showAppMenu: true,
        title: CurrentDepartment?.ou
          ? `View Department: ${CurrentDepartment?.ou}`
          : clickedDepartment?.ou
          ? `View Department: ${clickedDepartment?.ou}`
          : 'View Department',
        component: <DepartmentsTable />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/department/add-users',
      page: {
        showAppMenu: true,
        title: CurrentDepartment?.ou
          ? `View Department: ${CurrentDepartment?.ou}`
          : clickedDepartment?.ou
          ? `View Department: ${clickedDepartment?.ou}`
          : 'View Department',
        component: <DepartmentUserDetail />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/department/add-managers',
      page: {
        showAppMenu: true,
        title: CurrentDepartment?.ou
          ? `View Department: ${CurrentDepartment?.ou}`
          : clickedDepartment?.ou
          ? `View Department: ${clickedDepartment?.ou}`
          : 'View Department',
        component: <DepartmentManagerTable />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/department/add-apps',
      page: {
        showAppMenu: true,
        title: CurrentDepartment?.ou
          ? `View Department: ${CurrentDepartment?.ou}`
          : clickedDepartment?.ou
          ? `View Department: ${clickedDepartment?.ou}`
          : 'View Department',
        component: <DepartmentAppTable />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/users',
      page: {
        showAppMenu: true,
        title: 'Users',
        component: <Users />,
      },
    },
    {
      type: 'admin',
      path: `/user/edit`,
      page: {
        showAppMenu: true,
        title: `Edit User`,
        component: <User type='EditUser' />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },

    {
      type: 'admin',
      path: `/user/view`,
      page: {
        showAppMenu: true,
        title: `View User`,
        component: <User type='view' />,
      },
    },

    {
      type: 'admin',
      path: `/user/add`,
      page: {
        showAppMenu: true,
        title: ` Add User`,
        component: <User />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },

    {
      type: 'admin',
      path: '/departments',
      page: {
        showAppMenu: true,
        title: 'Departments',
        component: <Departments />,
      },
    },
    {
      type: 'admin',
      path: `/department/add`,
      page: {
        showAppMenu: true,
        title: `Add Department`,
        component: <Department />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: `/department/edit`,
      page: {
        showAppMenu: true,
        title: ` Edit Department`,
        component: <Department type='edit' />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/admins',
      page: {
        showAppMenu: true,
        title: 'Admins',
        component: <Administrators />,
        disclaimer: { show: !!(user && user?.permission === 'EndUser'), component: <AccessDeniedPage /> },
      },
    },
    {
      type: 'admin',
      path: '/admin/view',
      page: {
        showAppMenu: true,
        title: `View Admin`,
        component: <Admin type='view' />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/admin/add',
      page: {
        showAppMenu: true,
        title: `Add Admin`,
        component: <Admin type='add' />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/admin/edit',
      page: {
        showAppMenu: true,
        title: `Edit Admin`,
        component: <Admin type='edit' />,
        disclaimer: {
          show: !!((user && user?.permission === 'EndUser') || user?.promptMessage === true),
          component: <AccessDeniedPage />,
        },
      },
    },
    {
      type: 'admin',
      path: '/admin/profile',
      page: {
        showAppMenu: true,
        title: 'Profile',
        component: <Profile />,
      },
    },
    {
      type: 'admin',
      path: '/admin/dashboard',
      page: {
        showAppMenu: true,
        title: 'Dashboard',
        component: <EndUserDashboard />,
      },
    },
    {
      type: 'admin',
      path: '/admin/change-password',
      page: {
        showAppMenu: true,
        title: 'Password',
        component: <Password />,
      },
    },
    {
      type: 'admin',
      path: '/end-user/request',
      page: {
        showAppMenu: true,
        title: 'Requests End Users',
        component: <EndUserRequestTable />,
        // disclaimer: { show: !!(user && user?.permission === 'EndUser'), component: <AccessDeniedPage /> },
      },
    },
    {
      type: 'admin',
      path: '/admin/users-edit-request',
      page: {
        showAppMenu: true,
        title: 'Requests from End Users',
        component: <TabsEditRequest />,
        disclaimer: { show: !!(user && user?.permission === 'EndUser'), component: <AccessDeniedPage /> },
      },
    },
    {
      type: 'admin',
      path: '/dashboard',
      page: {
        showAppMenu: true,
        title: 'Dashboard',
        component: <Dashboard />,
        disclaimer: { show: !!(user && user?.permission === 'EndUser'), component: <AccessDeniedPage /> },
      },
    },
    { type: 'public', path: '/', page: { component: <SignIn /> } },
  ];

  return (
    <BrowserRouter>
      <Switch>
        {pages.map((page) => {
          const { type, path, page: pageConfig } = page;
          const { component, showAppMenu, title, extraInfo, disclaimer } = pageConfig;

          return (
            <ReactRoute key={path} exact path={path}>
              <RouteWrapper
                type={type}
                component={component}
                showAppMenu={showAppMenu}
                title={title}
                extraInfo={extraInfo}
                disclaimer={disclaimer}
              />
            </ReactRoute>
          );
        })}
        {/* Catch-all route for 404 - must be last */}
        {/* <ReactRoute path="*">
          <Welcome />
        </ReactRoute> */}
      </Switch>
    </BrowserRouter>
  );
});
